/**
 * QuickFilterChips - Utility filter chips (secondary to Discovery)
 * 
 * Design Principle: Subdued Utility Layer
 * - Quick Filters sind SEKUNDÄR zur CategoryDiscovery
 * - Keine Redundanz zum DeliveryModeToggle (kein Lieferung/Abholung Chip)
 * - Nur echte Sofortwert-Filter
 * 
 * Filter mit echtem Sofortwert:
 * 1. Jetzt geöffnet (grüner Pulse-Dot)
 * 2. In der Nähe (nur wenn Location)
 * 3. Top bewertet (4+ Sterne)
 */
import { memo, useCallback } from 'react';
import { MapPin, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFilterStore } from '@/lib/FilterStore';
import { useShallow } from 'zustand/react/shallow';

interface QuickFilterChipsProps {
  /** Whether user location is available - controls "In der Nähe" visibility */
  hasUserLocation?: boolean;
  className?: string;
}

type SortOption = 'distance' | 'rating' | 'Empfohlen';

export const QuickFilterChips = memo(function QuickFilterChips({
  hasUserLocation = false,
  className
}: QuickFilterChipsProps) {
  const { 
    openNow, 
    sortOption,
    setOpenNow, 
    setSortOption
  } = useFilterStore(useShallow((state) => ({
    openNow: state.openNow,
    sortOption: state.sortOption as SortOption,
    setOpenNow: state.setOpenNow,
    setSortOption: state.setSortOption
  })));

  // Toggle handlers
  const toggleOpenNow = useCallback(() => {
    setOpenNow(!openNow);
  }, [openNow, setOpenNow]);

  const toggleNearby = useCallback(() => {
    setSortOption(sortOption === 'distance' ? 'Empfohlen' : 'distance');
  }, [sortOption, setSortOption]);

  const toggleTopRated = useCallback(() => {
    setSortOption(sortOption === 'rating' ? 'Empfohlen' : 'rating');
  }, [sortOption, setSortOption]);

  // Active states
  const isNearbyActive = sortOption === 'distance';
  const isTopRatedActive = sortOption === 'rating';

  // Stronger chip styling - clear secondary controls
  const getChipClasses = (isActive: boolean) => cn(
    "flex-shrink-0 inline-flex items-center gap-2 rounded-full",
    "min-w-fit whitespace-nowrap",
    "text-sm font-semibold",
    "px-4 py-2",
    "snap-start",
    "touch-manipulation select-none transition-colors duration-150",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 focus-visible:ring-offset-1",
    isActive 
      ? "bg-gray-800 text-white shadow-sm" 
      : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 hover:border-gray-300"
  );

  const iconClasses = "w-3.5 h-3.5";

  return (
    <div 
      className={cn(
        "flex gap-2 py-0.5",
        "flex-nowrap overflow-x-auto scrollbar-hide",
        "snap-x snap-mandatory",
        "sm:flex-wrap sm:overflow-x-visible",
        className
      )}
      role="group"
      aria-label="Schnellfilter"
    >
      {/* 1. Jetzt geöffnet - mit grünem Pulse-Dot */}
      <button 
        onClick={toggleOpenNow}
        className={getChipClasses(openNow)}
        aria-pressed={openNow}
        type="button"
      >
        <span className="relative flex h-2 w-2" aria-hidden="true">
          <span className={cn(
            "absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping",
            openNow ? "bg-white" : "bg-[#009544]"
          )} />
          <span className={cn(
            "relative inline-flex rounded-full h-2 w-2",
            openNow ? "bg-white" : "bg-[#009544]"
          )} />
        </span>
        <span>Jetzt geöffnet</span>
      </button>

      {/* 2. In der Nähe - nur wenn Location verfügbar */}
      {hasUserLocation && (
        <button 
          onClick={toggleNearby}
          className={getChipClasses(isNearbyActive)}
          aria-pressed={isNearbyActive}
          type="button"
        >
          <MapPin className={iconClasses} aria-hidden="true" />
          <span>In der Nähe</span>
        </button>
      )}

      {/* 3. Top bewertet */}
      <button 
        onClick={toggleTopRated}
        className={getChipClasses(isTopRatedActive)}
        aria-pressed={isTopRatedActive}
        type="button"
      >
        <Star className={cn(iconClasses, isTopRatedActive && "fill-current")} aria-hidden="true" />
        <span>Top bewertet</span>
      </button>
    </div>
  );
});
